import {
  OwnerType,
  TOwner,
  TOwnerLink,
  TOwnerNode,
  TOwnerRange,
  TOwnershipStructure,
} from '../types.ts';
import { v4 as uuidv4 } from 'uuid';
import { getAddressInitialValues, getDocumentsInitialValues } from '../../../utils.ts';
import {
  defaultFormAddress,
  emptyPersonalDocumentInfo,
  ukraineOption,
} from '../../../constants.ts';
import { CustomResolveOptions, TAddress, TPersonalDocument } from '../../../types.ts';
import { Business, Diversity3, Person, RequestPage } from '@mui/icons-material';
import {
  EMPTY_RANGE,
  entitiesWithSimilarFields,
  individualTypes,
  ownerLabelText,
  ROOT_OWNER_NODE,
} from '../constants.ts';
import { TDocument } from '~/modules/identification/forms/Documents/types.ts';
import { SvgIconProps } from '@mui/material';
import { FieldPath, FieldPathValue, FieldValues, UseFormSetValue } from 'react-hook-form';
import { getValidationFormValue } from '~/helpers/utils.ts';
import { TDocumentRequestData } from '~/store/identification-form/types.ts';
import { WEB_DOCS, WebDocName } from '~/constants/docs.ts';
import { AppDispatch } from '~/store/store.ts';
import { addDocument, deleteDocument } from '~/store/identification-form/thunks/documentThunks.ts';

export const addTenYears = (date: string) =>
  date
    .split('.')
    .map((item, index) => (index === 2 ? Number(item) + 10 : item))
    .join('.');

export const getEmptyOwner = (type?: OwnerType): TOwner => {
  const currentType = type || OwnerType.INDIVIDUAL;
  const residenceCountry = entitiesWithSimilarFields.includes(currentType) ? ukraineOption : null;
  const address = entitiesWithSimilarFields.includes(currentType) ? defaultFormAddress : null;
  const fullName = entitiesWithSimilarFields.includes(currentType)
    ? null
    : ownerLabelText[type as OwnerType];
  const isWithoutFormalOwnership = currentType === OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP;
  const isIndividualType = individualTypes.includes(currentType);
  const isShareholderGroup = type === OwnerType.SHAREHOLDER_GROUP;

  return {
    id: uuidv4(),
    lastName: null,
    firstName: null,
    middleName: null,
    ownershipPercentage: null,
    identificationNumber: null,
    foreignTaxNumber: null,
    type: currentType,
    residenceCountry: residenceCountry,
    citizenshipCountry: isIndividualType ? ukraineOption : null,
    birthplaceCountry: isIndividualType ? ukraineOption : null,
    birthPlace: null,
    documents: isIndividualType ? emptyPersonalDocumentInfo : null,
    birthDate: null,
    address: address,
    isUSTaxResident: false,
    isOtherTaxResident: false,
    isBeneficiary: isIndividualType ? isWithoutFormalOwnership || null : false,
    isInBankBlackList: false,
    isInTerList: false,
    linksLabels: null,
    fullName: fullName,
    shortNameUkr: null,
    rangeLegal: isShareholderGroup ? EMPTY_RANGE : null,
    rangeIndividual: isShareholderGroup ? EMPTY_RANGE : null,
    russianBelarusianIndividuals: isShareholderGroup ? [] : null,
    russianBelarusianLegalEntities: isShareholderGroup ? [] : null,
    sourceURL: null,
    registrationDate: null,
    typesOfActivity: null,
    hasRussianOrBelarusianCitizenShareholders: null,
    areIndividualsWithDecisiveInfluenceAbsent: false,
    isRegardlessOfFormalOwnership: isWithoutFormalOwnership,
  };
};

export const getEmptyLink = (
  fromNodeId: string,
  toNodeId: string,
  parentLinkId: string | null = null,
  ownershipPercentage: number | null = null,
  displayOrder = 0
): TOwnerLink => ({
  id: uuidv4(),
  fromNodeId,
  toNodeId,
  ownershipPercentage,
  parentLinkId,
  displayOrder,
});

export const getInitialValues = (
  formData: TOwnershipStructure,
  isFeatureOff?: boolean
): TOwnershipStructure => {
  return {
    owners:
      formData?.owners?.length > 0
        ? formData.owners.map((owner: TOwner) => getOwnerData(owner, isFeatureOff))
        : formData?.isFoundersAndBeneficiariesAbsent
          ? []
          : [getEmptyOwner()],
    ownershipPercentageTotal: formData?.ownershipPercentageTotal
      ? Number(formData.ownershipPercentageTotal)
      : 0,
    isBeneficiariesAbsent: formData?.isBeneficiariesAbsent || false,
    link: isFeatureOff
      ? !formData.link.length && !!formData.owners.length
        ? [getEmptyLink(uuidv4(), ROOT_OWNER_NODE)]
        : formData.link
      : formData.link || [],
    ownershipStructureSpecifics: formData.ownershipStructureSpecifics || '',
    isFoundersAndBeneficiariesAbsent: formData?.isFoundersAndBeneficiariesAbsent || false,
  };
};

export const getOwnerData = (owner: TOwner, isFeatureOff?: boolean) => {
  const isIndividualType = individualTypes.includes(owner?.type);
  const isWithoutFormalOwnership = owner?.type === OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP;
  const isIndividOrLegal = entitiesWithSimilarFields.includes(owner?.type);
  const citizenshipCountry = owner?.citizenshipCountry || (isIndividualType ? ukraineOption : null);
  const birthplaceCountry = owner?.birthplaceCountry || (isIndividualType ? ukraineOption : null);
  const residenceCountry = owner?.residenceCountry || (isIndividualType ? ukraineOption : null);

  return {
    id: owner.id || uuidv4(),
    lastName: owner?.lastName || '',
    firstName: owner?.firstName || '',
    middleName: owner?.middleName || '',
    ownershipPercentage: owner?.ownershipPercentage || '',
    identificationNumber: owner?.identificationNumber || '',
    foreignTaxNumber: owner?.foreignTaxNumber || '',
    citizenshipCountry: citizenshipCountry,
    residenceCountry: residenceCountry,
    birthplaceCountry: birthplaceCountry,
    birthPlace: owner?.birthPlace || '',
    documents: isIndividOrLegal
      ? getDocumentsInitialValues(owner as { documents: TPersonalDocument })
      : null,
    birthDate: owner?.birthDate || null,
    address: getAddressInitialValues(owner as { address: TAddress }),
    isUSTaxResident: !!owner?.isUSTaxResident,
    isOtherTaxResident: !!owner?.isOtherTaxResident,
    isInTerList: owner?.isInTerList ?? false,
    isInBankBlackList: owner?.isInBankBlackList ?? false,
    isBeneficiary: owner?.isBeneficiary ?? (isWithoutFormalOwnership || null),
    type: isFeatureOff ? OwnerType.INDIVIDUAL : owner.type,
    linksLabels: owner.linksLabels || null,
    fullName: owner.fullName || null,
    shortNameUkr: owner.shortNameUkr || null,
    rangeLegal: owner.rangeLegal || null,
    rangeIndividual: owner.rangeIndividual || null,
    sourceURL: owner.sourceURL || null,
    registrationDate: owner.registrationDate || null,
    typesOfActivity: owner.typesOfActivity || null,
    russianBelarusianIndividuals: owner.russianBelarusianIndividuals,
    russianBelarusianLegalEntities: owner.russianBelarusianLegalEntities,
    hasRussianOrBelarusianCitizenShareholders:
      owner.hasRussianOrBelarusianCitizenShareholders ?? null,
    isRegardlessOfFormalOwnership: owner.isRegardlessOfFormalOwnership || false,
    areIndividualsWithDecisiveInfluenceAbsent:
      owner.areIndividualsWithDecisiveInfluenceAbsent || false,
  };
};

export const gerParsedValues = (values: TOwnershipStructure) => ({
  ...values,
  ownershipPercentageTotal: values.ownershipPercentageTotal?.toString() || '0',
  link: values.link.map((item) => ({
    ...item,
    ownershipPercentage: +(item.ownershipPercentage || 0),
  })) as TOwnerLink[],
  owners: values.owners.map((item) => {
    if (item.type === OwnerType.SHAREHOLDER_GROUP) {
      return {
        ...item,
        rangeLegal: item.rangeLegal?.number
          ? ({
              ...item.rangeLegal,
              from: +(item.rangeLegal?.from || 0),
              to: +(item.rangeLegal?.to || 0),
            } as TOwnerRange)
          : null,
        rangeIndividual: item.rangeIndividual?.number
          ? ({
              ...item.rangeIndividual,
              from: +(item.rangeIndividual?.from || 0),
              to: +(item.rangeIndividual?.to || 0),
            } as TOwnerRange)
          : null,
      };
    }
    return item;
  }),
});

export const getSiblingLinks = (links: TOwnerLink[], parentLinkId?: string | null): TOwnerLink[] =>
  links
    .filter((link) => link.parentLinkId === parentLinkId)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

export const buildTree = (owners: TOwner[], links: TOwnerLink[]): TOwnerNode[] => {
  if (!owners?.length || !links?.length) return [];

  const ownerById = new Map<string, { owner: TOwner; fieldIndex: number }>();
  owners.forEach((owner, index) => ownerById.set(owner.id, { owner, fieldIndex: index }));

  const linkIndexById = new Map<string, number>();
  links.forEach((link, index) => linkIndexById.set(link.id, index));

  const childrenByParent = new Map<string, TOwnerLink[]>();
  links.forEach((link) => {
    const key = link.parentLinkId || ROOT_OWNER_NODE;
    const bucket = childrenByParent.get(key) ?? [];
    bucket.push(link);
    childrenByParent.set(key, bucket);
  });
  childrenByParent.forEach((bucket) =>
    bucket.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
  );

  const buildNode = (
    link: TOwnerLink,
    level: number,
    ancestorOwnerIds: Set<string>,
    ancestorLinkIds: string[]
  ): TOwnerNode | null => {
    const entry = ownerById.get(link.fromNodeId);
    if (!entry) return null;

    const isCyclic = ancestorOwnerIds.has(link.fromNodeId);

    const node: TOwnerNode = {
      ...entry.owner,
      fieldIndex: entry.fieldIndex,
      linkIndex: linkIndexById.get(link.id) ?? -1,
      linkId: link.id,
      parentLinkId: link.parentLinkId ?? null,
      ancestorLinkIds,
      isCyclic,
      level,
      children: [],
    };

    if (!isCyclic) {
      const nextAncestors = new Set(ancestorOwnerIds);
      nextAncestors.add(link.fromNodeId);
      const nextAncestorLinks = [...ancestorLinkIds, link.id];

      node.children = (childrenByParent.get(link.id) ?? [])
        .map((childLink) => buildNode(childLink, level + 1, nextAncestors, nextAncestorLinks))
        .filter((child): child is TOwnerNode => !!child);
    }

    return node;
  };

  return (childrenByParent.get(ROOT_OWNER_NODE) ?? [])
    .map((rootLink) => buildNode(rootLink, 1, new Set(), []))
    .filter((node): node is TOwnerNode => !!node);
};

export const getOwnerTypeIcon = (type?: OwnerType, props?: SvgIconProps) => {
  switch (type) {
    case OwnerType.INDIVIDUAL:
      return <Person color="primary" {...props} />;
    case OwnerType.LEGAL_ENTITY:
      return <Business color="primary" {...props} />;
    case OwnerType.FREE_FLOAT:
      return <RequestPage color="primary" {...props} />;
    case OwnerType.SHAREHOLDER_GROUP:
      return <Diversity3 color="primary" {...props} />;
    case OwnerType.BENEFICIARY_WITHOUT_FORMAL_OWNERSHIP:
      return <Person color="primary" {...props} />;
    default:
      return <Person color="primary" {...props} />;
  }
};

export const getChildIndexes = (node: TOwnerNode): { indexes: number[]; linkIndexes: number[] } => {
  const indexes = [];
  const linkIndexes = [];
  for (const child of node.children || []) {
    indexes.push(child.fieldIndex);
    linkIndexes.push(child.linkIndex);
    indexes.push(...getChildIndexes(child).indexes);
    linkIndexes.push(...getChildIndexes(child).linkIndexes);
  }

  return { indexes, linkIndexes };
};

export const getIsFirstLevel = (options: CustomResolveOptions, id: string) => {
  const links = getValidationFormValue<TOwnershipStructure>(options, 1)?.link;
  if (!links?.length) return true;
  const ownerLinks = getValidationFormValue<TOwnershipStructure>(options, 1)?.link.filter(
    (item: TOwnerLink) => item.fromNodeId === id
  );
  return ownerLinks?.some((item: TOwnerLink) => item.toNodeId === ROOT_OWNER_NODE);
};

export const findDocumentById = (documents: TDocument[], targetId: number) => {
  return documents?.find((doc) => doc.idDictWebDocument === targetId) || null;
};

export function setAllFieldsTouched<T extends FieldValues, N extends FieldPath<T>>(
  path: N,
  values: FieldPathValue<T, N>,
  setValue: UseFormSetValue<T>
) {
  if (values == null) return;

  if (Array.isArray(values)) {
    values.forEach((item: FieldPathValue<T, N>, index: number) => {
      const currentPath = `${path}.${index}`;
      setValue(currentPath as N, item, { shouldTouch: true, shouldValidate: true });
      if (typeof item === 'object' && item !== null) {
        setAllFieldsTouched(currentPath as N, item, setValue);
      }
    });
    return;
  }

  if (typeof values === 'object') {
    Object.entries(values).forEach(([key, val]) => {
      const value = val as FieldPathValue<T, N>;
      const currentPath = path ? `${path}.${key}` : key;
      setValue(currentPath as N, value, { shouldTouch: true, shouldValidate: true });
      if (typeof value === 'object' && value !== null) {
        setAllFieldsTouched(currentPath as N, value, setValue);
      }
    });
  }
}

export const getAllChildLinks = (id: string, links: TOwnerLink[]): TOwnerLink[] => {
  const directChildLinks = links.filter((l) => l.toNodeId === id);
  return [
    ...directChildLinks,
    ...directChildLinks.flatMap((l) => getAllChildLinks(l.fromNodeId, links)),
  ];
};

export const getOwnerName = (owenr?: TOwner | null) => {
  return (
    (`${owenr?.lastName || ''} ${owenr?.firstName || ''} ${owenr?.middleName || ''}`.trim() ||
      owenr?.fullName) ??
    owenr?.shortNameUkr ??
    ''
  );
};

export const findNodeByLinkId = (linkId: string | null, tree: TOwnerNode[]): TOwnerNode | null => {
  if (!linkId) return null;
  for (const node of tree) {
    if (node.linkId === linkId) return node;
    const found = findNodeByLinkId(linkId, node.children);
    if (found) return found;
  }
  return null;
};

export const buildSubtreeUnder = (
  parentLink: TOwnerLink | null,
  owner: TOwner,
  parentId: string,
  allLinks: TOwnerLink[],
  sibilingLength: number,
  sourceLink?: TOwnerLink | null
): TOwnerLink[] => {
  const built: TOwnerLink[] = [];

  const rootLink: TOwnerLink = {
    ...getEmptyLink(owner.id, parentLink ? parentLink.fromNodeId : parentId),
    parentLinkId: parentLink ? parentLink.id : null,
    displayOrder: parentLink
      ? allLinks.filter((l) => l.parentLinkId === parentLink.id).length
      : sibilingLength,
  };
  built.push(rootLink);

  if (sourceLink) {
    const cloneChildren = (oldParentLinkId: string, newParentLinkId: string) => {
      allLinks
        .filter((link) => link.parentLinkId === oldParentLinkId)
        .forEach((child) => {
          const newId = uuidv4();
          built.push({ ...child, id: newId, parentLinkId: newParentLinkId });
          cloneChildren(child.id, newId);
        });
    };
    cloneChildren(sourceLink.id, rootLink.id);
  }

  return built;
};

export const updateOwnershipStructureSchema = async (
  dispatch: AppDispatch,
  pdfFile: File,
  mainDocuments: TDocument[],
  appId: number | string,
  shouldRegenerate?: boolean
) => {
  const requestData: TDocumentRequestData = {
    docType: WEB_DOCS[WebDocName.ownershipStructureScheme],
    document: pdfFile,
  };

  const targetDocument = findDocumentById(
    mainDocuments,
    WEB_DOCS[WebDocName.ownershipStructureScheme]
  );

  if (shouldRegenerate) {
    if (targetDocument?.idDoc) {
      await dispatch(
        deleteDocument({
          applicationId: appId as number,
          idDoc: targetDocument.idDoc,
          blockId: 1,
        })
      );
    }
    await dispatch(
      addDocument({
        applicationId: appId as number,
        data: { requestData, blockId: 1 },
      })
    );
  } else {
    if (targetDocument?.idDoc) {
      await dispatch(
        deleteDocument({
          applicationId: appId as number,
          idDoc: targetDocument.idDoc,
          blockId: 1,
        })
      );
    }
  }
};
